import { createContext, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useReducer } from 'react';
import reducer, { CollectionsActions, CollectionsContextState, initialState } from './reducer';
import useUrlState from '../../../../../../Hooks/useUrlState';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';
import { FCWithChildren } from '../../../../../../App.types';
import {
    TiktokAudioCollection,
    getCollections,
    getTiktokAudioCollectionHeadlineStats,
    GetTiktokAudioCollectionAudioStatsParams,
    TiktokAudioCollectionPostStats,
} from '@round/api';

type CollectionsContextValues = CollectionsContextState & {
    urlState: Partial<Record<keyof UrlState, string>>;
};

type CollectionsContextUtils = {
    setUrlState: (s: SetStateAction<Partial<UrlState>>) => void;
    setSelectedCollection: (collection: TiktokAudioCollection | undefined) => void;
    dispatch: Dispatch<CollectionsActions>;
    loadMoreCollections: () => Promise<void>;
    addCollection: (collection: TiktokAudioCollection) => void;
    updateCollection: (collection: TiktokAudioCollection) => void;
};

type UrlState = {
    catalogueId?: number;
    search?: string;
    aP: number;
    aPS: number;
    aS: string;
    aO: GetTiktokAudioCollectionAudioStatsParams['ordering'];
    audioModal?: number;
    vP: number;
    vPS: number;
    vS: string;
    vOD: keyof TiktokAudioCollectionPostStats | undefined;
    vOA: keyof TiktokAudioCollectionPostStats | undefined;
    showSuggestions?: boolean;
};

export const CollectionsContext = createContext<[CollectionsContextValues, CollectionsContextUtils] | null>(null);
export const CollectionsContextProvider: FCWithChildren = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [urlState, setUrlState] = useUrlState<UrlState>({
        aP: 1,
        aPS: 10,
        aS: '',
        aO: '-video_count_daily_change',
        vP: 1,
        vPS: 10,
        vS: '',
        vOD: 'play_count',
        vOA: undefined,
    });

    const setSelectedCollection = useCallback(
        (collection: TiktokAudioCollection | undefined) => {
            setUrlState({ catalogueId: collection?.id });
            dispatch({ type: 'setSelectedCollection', payload: collection });
        },
        [setUrlState]
    );

    const fetchCollectionOptions = useCallback(async (...args: Parameters<typeof getCollections>) => {
        try {
            dispatch({ type: 'loadCollectionOptions' });
            const response = await getCollections(...args);
            dispatch({ type: 'loadedCollectionOptions', payload: response.data });
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            dispatch({ type: 'errorLoadingCollectionOptions' });
        }
    }, []);

    const loadMoreCollections = useCallback(
        async (requestInit?: RequestInit) => {
            if (!state.hasNextCollectionsPage || state.collectionsLoading) {
                return;
            }

            try {
                dispatch({ type: 'loadCollectionOptions' });
                const response = await getCollections({ page: state.collectionsPage + 1 }, requestInit);
                dispatch({ type: 'loadMoreCollections', payload: response.data });
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }
                dispatch({ type: 'errorLoadingCollectionOptions' });
            }
        },
        [state.collectionsLoading, state.collectionsPage, state.hasNextCollectionsPage]
    );

    const fetchHeadlineStats = useCallback(async (collectionId: number, requestInit?: RequestInit) => {
        try {
            dispatch({ type: 'setHeadlineStatsLoading', payload: true });
            const response = await getTiktokAudioCollectionHeadlineStats(collectionId, requestInit);
            if (response.status === 200) {
                dispatch({ type: 'loadedHeadlineStats', payload: response.data });
            }
        } catch {
            // no-op
        } finally {
            dispatch({ type: 'setHeadlineStatsLoading', payload: false });
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            fetchCollectionOptions({ page: 1, page_size: 25, search: urlState.search }, { signal });
        },
        [urlState.search, fetchCollectionOptions]
    );

    useAbortableEffect(
        (signal) => {
            if (state.selectedCollection) {
                fetchHeadlineStats(state.selectedCollection.id, { signal });
            }
        },
        [state.selectedCollection, fetchHeadlineStats]
    );

    useEffect(() => {
        if (urlState.catalogueId && state.collections.length && !state.selectedCollection) {
            setSelectedCollection(state.collections.find((c) => c.id === Number(urlState.catalogueId)));
            return;
        }

        if (!urlState.catalogueId && state.collections.length) {
            setSelectedCollection(state.collections[0]);
        }
    }, [setSelectedCollection, state.collections, state.selectedCollection, urlState.catalogueId]);

    const providerValues: CollectionsContextValues = useMemo(
        () => ({
            ...state,
            urlState,
        }),
        [state, urlState]
    );

    const utils: CollectionsContextUtils = useMemo(
        () => ({
            setUrlState,
            setSelectedCollection,
            dispatch,
            loadMoreCollections,
            addCollection: (collection) => dispatch({ type: 'addCollection', payload: collection }),
            updateCollection: (collection) => dispatch({ type: 'updateCollection', payload: collection }),
        }),
        [loadMoreCollections, setSelectedCollection, setUrlState]
    );

    return <CollectionsContext.Provider value={[providerValues, utils]}>{children}</CollectionsContext.Provider>;
};
